import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchAllAttendees, setAllAttendeesPage } from '@/store/slices/audiences';
import {
  selectAllAttendeesFields,
  selectAllAttendeesPage,
  selectAllAttendeesPageSize,
  selectAllAttendeesSearch,
  selectAllAttendeesSort,
  selectAllAttendeesSortDirection,
} from '@/store/slices/audiences/selectors';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel } from '@/store/slices/user/selectors';
import type { RegistrationFilter, UserStatus } from '@/types/all-attendees.types';
import { useEffect, useRef } from 'react';

export const useLoadAllAttendeesList = (lockedStatus?: UserStatus, registrationStatusFilter?: RegistrationFilter) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectActiveChannel);
  const eventUuid = useAppSelector(selectWorkingProjectUuid);
  const page = useAppSelector(selectAllAttendeesPage);
  const pageSize = useAppSelector(selectAllAttendeesPageSize);
  const search = useAppSelector(selectAllAttendeesSearch);
  const sort = useAppSelector(selectAllAttendeesSort);
  const sortDirection = useAppSelector(selectAllAttendeesSortDirection);
  const fields = useAppSelector(selectAllAttendeesFields);

  // Both filters come from the URL (the :listType route param and the registrationStatus search
  // param), so they can change without a remount. A filter change invalidates the current page:
  // reset to page 1 and skip this fetch - the page update re-runs the effect, which then fetches
  // once with the new filters. (Dispatching the fetch in the same pass would be dropped by the
  // thunk's in-flight guard, leaving the stale page.)
  const previousFilters = useRef({ lockedStatus, registrationStatusFilter });

  useEffect(() => {
    if (!eventUuid) return;

    const filtersChanged =
      previousFilters.current.lockedStatus !== lockedStatus ||
      previousFilters.current.registrationStatusFilter !== registrationStatusFilter;
    previousFilters.current = { lockedStatus, registrationStatusFilter };

    if (filtersChanged && page !== 1) {
      dispatch(setAllAttendeesPage(1));
      return;
    }

    dispatch(fetchAllAttendees({ fields, status: lockedStatus, registrationStatus: registrationStatusFilter }));
  }, [
    channel,
    eventUuid,
    page,
    pageSize,
    search,
    sort,
    sortDirection,
    fields,
    lockedStatus,
    registrationStatusFilter,
    dispatch,
  ]);
};
